import { Button, ConfirmModal, EllipsisVerticalIcon, GridDisplay, Label, Popover, WarningIcon } from "@wexinc/wex-ui";
import React, { useState } from "react";
import { Cell, Column } from "react-table";
import { OrganizationInvitation } from "services/organization";

export type OnInvitationAction = (action: InvitationActions, invitation: OrganizationInvitation) => void;
export type InvitationActions = "revoke";

interface Props {
  canPerformInviteActions: boolean;
  organizationInvites: OrganizationInvitation[];
  onInvitationAction: OnInvitationAction;
}

const OrganizationInvitesGrid: React.FC<Props> = (props) => {
  const { canPerformInviteActions, organizationInvites, onInvitationAction } = props;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<{
    handler: () => void;
  }>();

  function onInvitationActionInternal(action: InvitationActions, invitation: OrganizationInvitation) {
    const doIt = () => {
      onInvitationAction(action, invitation);
      setShowDeleteConfirmation(undefined);
    };
    if (action === "revoke") {
      setShowDeleteConfirmation({
        handler: () => doIt(),
      });
    } else {
      doIt();
    }
  }

  const columns = React.useMemo(
    () => getColumns(canPerformInviteActions, onInvitationActionInternal),
    [],
  ) as unknown as Array<Column<OrganizationInvitation>>;

  return (
    <>
      <GridDisplay
        memoizedColumns={columns}
        memoizedData={organizationInvites}
      />
      {showDeleteConfirmation && (
        <ConfirmModal
          onOk={showDeleteConfirmation.handler}
          onCancel={() => setShowDeleteConfirmation(undefined)}
          title="Are you sure you wish to revoke this invitation?"
          mode="critical"
          modalClassNames="!max-w-xl"
        />
      )}
    </>
  );
};

export default OrganizationInvitesGrid;

function getColumns(canPerformInvitationActions: boolean, onAction: OnInvitationAction) {
  const result = [];
  if (canPerformInvitationActions) {
    result.push({
      Header: () => null,
      width: 24,
      id: "actions",
      Cell: ({ row }: Cell<OrganizationInvitation>) => {
        return (
          <ActionsMenu
            organizationInvite={row.original}
            onAction={onAction}
          />
        );
      },
    });
  }
  result.push(
    ...[
      {
        Header: "EMAIL",
        accessor: "email",
        width: "250px",
        Cell: ({ row }: Cell<OrganizationInvitation>) => {
          return (
            <p
              className="truncate max-w-[30ch]"
              title={row.original.email.length >= 30 ? `${row.original.email}` : undefined}
            >
              {row.original.email}
            </p>
          );
        },
      },
      {
        Header: "SENT ON",
        accessor: "createdAt",
        width: "150px",
        Cell: ({ row }: Cell<OrganizationInvitation>) => {
          const isExpired = row.original.expiresAt.getTime() < Date.now();
          return (
            <div
              className="flex items-center"
              title={isExpired ? "Invitation has expired." : undefined}
            >
              <Label className="max-w-[100px]">{row.original.createdAt.toLocaleString()}</Label>
              {isExpired && <WarningIcon className="text-utility-warning-50 fill-current" />}
            </div>
          );
        },
      },
    ],
  );

  return result;
}

interface ActionsMenuProps {
  organizationInvite: OrganizationInvitation;
  onAction: OnInvitationAction;
}

const ActionsMenu: React.FC<ActionsMenuProps> = (props) => {
  return (
    <Popover
      testId="show-metric-actions-menu"
      className="w-34 p-2"
      anchorButtonClassName="w-8 h-8 flex justify-center items-center"
      getAnchorContent={() => (
        <div>
          <EllipsisVerticalIcon />
        </div>
      )}
    >
      {() => (
        <>
          <Button
            testId="revoke-invitation"
            buttonType="secondary-neutral"
            className="w-full"
            onClick={() => props.onAction("revoke", props.organizationInvite)}
          >
            Revoke invitation
          </Button>
        </>
      )}
    </Popover>
  );
};
